import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import type { AxiosResponse } from 'axios';
import type ApiBathCard from 'src/core/models/api/ApiBathCard';
import type ApiBathCardUsps from 'src/core/models/api/ApiBathCardUsps';
import type ApiPromotion from 'src/core/models/api/ApiPromotion';
import type ApiBathCardsResponse from 'src/core/models/api/responses/ApiBathCardsResponse';
import type ApiErrorResponse from 'src/core/models/api/responses/ApiErrorResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import { clamp } from 'src/core/utils/Numbers';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const clampPage = (page: number, cardCount: number): number => clamp(page, 1, Math.max(cardCount, 1));

const byExpiryDate = (cards: ApiBathCard[]): ApiBathCard[] =>
	[...cards].sort((a, b) => {
		if (a.expire_at === b.expire_at) {
			return 0;
		}

		if (a.expire_at === null) {
			return 1;
		}

		if (b.expire_at === null) {
			return -1;
		}

		return a.expire_at.localeCompare(b.expire_at);
	});

const initialState = {
	bathCards: null as ApiBathCard[] | null,
	usps: null as ApiBathCardUsps | null,
	promotion: null as ApiPromotion | null,
	cardsPage: 1,
	isLoading: false,
	isAddingCard: false,
	isRemovingCard: false,
	hasError: false,
	addCardErrorMessage: null as string | null,
	addCardBarcode: '',
	selectedWorkspaceId: '',
	removeCard: null as ApiBathCard | null,
	isAddCardDialogVisible: false,
	isRemoveCardDialogVisible: false,
};

export const useBathCardsStore = create(
	combine({ ...initialState }, (set, get) => {
		const init = () =>
			set({
				addCardErrorMessage: null,
				addCardBarcode: '',
				selectedWorkspaceId: '',
				removeCard: null,
				isAddCardDialogVisible: false,
				isRemoveCardDialogVisible: false,
			});

		const getApiBathCards = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: (response: AxiosResponse<ApiBathCardsResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			if (get().bathCards === null) {
				set({ isLoading: true });
			}
			set({ hasError: false });

			await ThermenRepository.getBathCards()
				.then(async (response) => {
					const responseData: ApiBathCardsResponse | null = response.data;
					const bathCards = byExpiryDate(responseData?.data ?? []);
					set({
						bathCards,
						usps: responseData?.bath_card_usps ?? null,
						promotion: responseData?.promotion ?? null,
						cardsPage: clampPage(get().cardsPage, bathCards.length),
					});
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ hasError: true });
					reportToCallerOrCrashlytics({ message: 'Fetching the bath cards failed.', error, onError });
				})
				.finally(() => {
					set({ isLoading: false });
					onFinally?.();
				});
		};

		const postApiBathCard = async ({
			workspaceId,
			barcode,
			onSuccess,
			onError,
			onFinally,
		}: {
			workspaceId: string;
			barcode: string;
			onSuccess?: (response: AxiosResponse<ApiBathCardsResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isAddingCard: true, addCardErrorMessage: null });

			await ThermenRepository.addBathCard(workspaceId, barcode)
				.then(async (response) => {
					set({ isAddingCard: false });
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					const serverMessage = isAxiosError<ApiErrorResponse>(error) ? error.response?.data?.message : undefined;
					const message = serverMessage ?? toErrorMessage(error);
					set({ isAddingCard: false, addCardErrorMessage: message });
					reportToCallerOrCrashlytics({ message: 'Adding a bath card failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const deleteApiBathCard = async ({
			cardId,
			onSuccess,
			onError,
			onFinally,
		}: {
			cardId: string;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isRemovingCard: true });

			await ThermenRepository.deleteBathCard(cardId)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
				.finally(() => {
					set({ isRemovingCard: false });
					onFinally?.();
				});
		};

		const confirmRemoveCard = async () => {
			const card = get().removeCard;

			if (card === null) {
				set({ isRemoveCardDialogVisible: false });

				return;
			}

			await deleteApiBathCard({
				cardId: String(card.id),
				onSuccess: async () => {
					if (get().removeCard?.id === card.id) {
						set({ isRemoveCardDialogVisible: false, removeCard: null });
					}

					await getApiBathCards();
				},
				onError: (error) => reportToCallerOrCrashlytics({ message: 'Deleting a bath card failed.', error }),
			});
		};

		const openAddCardDialog = () => set({ addCardErrorMessage: null, isAddCardDialogVisible: true });

		const closeAddCardDialog = () =>
			set({ isAddCardDialogVisible: false, addCardErrorMessage: null, addCardBarcode: '', selectedWorkspaceId: '' });

		const openRemoveCardDialog = (removeCard: ApiBathCard) => set({ removeCard, isRemoveCardDialogVisible: true });

		const setRemoveCardDialogVisible = (isRemoveCardDialogVisible: boolean) => set({ isRemoveCardDialogVisible });

		const setAddCardBarcode = (addCardBarcode: string) => set({ addCardBarcode });

		const setSelectedWorkspaceId = (selectedWorkspaceId: string) => set({ selectedWorkspaceId });

		const setAddCardErrorMessage = (message: string | null) => set({ addCardErrorMessage: message });

		const setCardsPage = (cardsPage: number) => set({ cardsPage });

		const reset = () => set({ ...initialState });

		return {
			init,
			getApiBathCards,
			postApiBathCard,
			deleteApiBathCard,
			confirmRemoveCard,
			openAddCardDialog,
			closeAddCardDialog,
			openRemoveCardDialog,
			setRemoveCardDialogVisible,
			setAddCardBarcode,
			setSelectedWorkspaceId,
			setAddCardErrorMessage,
			setCardsPage,
			reset,
		};
	}),
);
